'use client'

// Next Imports
import dynamic from 'next/dynamic'

// MUI Imports
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import { useTheme } from '@mui/material/styles'

// Third-party Imports
import type { ApexOptions } from 'apexcharts'

// Types
import type { CategoryChartProps, CategoryData } from '@/types/dashboards/resumeTypes'

// Styled Component Imports
const AppReactApexCharts = dynamic(() => import('@/libs/styles/AppReactApexCharts'))

// Hooks
import { useDashboardResumeTranslations } from '@/hooks/useTranslations'
import { useCategoryLookup } from '@/hooks/api/useCategoryLookup'

// Cores para as categorias de despesa
const EXPENSE_COLORS = [
  '#4CAF50', // Verde - Insumos Agrícolas / other
  '#2196F3', // Azul - Maquinário e Equipamentos
  '#FF9800', // Laranja - Funcionários / fuel
  '#9C27B0', // Roxo - Combustível
  '#F44336', // Vermelho - Manutenção
  '#795548', // Marrom - Arrendamento
  '#607D8B', // Azul Acinzentado - Outros
  '#FFC107'  // Amarelo - Financiamento
]

// Cores para as categorias de receita
const REVENUE_COLORS = [
  '#4CAF50', // Verde - Venda de Grãos / grainSales
  '#2196F3', // Azul - Prestação de Serviços
  '#FF9800', // Laranja - Arrendamento / other
  '#9C27B0', // Roxo - Venda de Gado
  '#00BCD4', // Ciano - Subsídios
  '#795548', // Marrom - Outros
]

// Mapeamento removido - agora usando traduções centralizadas do arquivo common

// Mapeamento de cores removido - agora usando cores baseadas em índice

const CategoryChart = ({ title, data, loading = false, type }: CategoryChartProps) => {
  const { t } = useDashboardResumeTranslations()
  const theme = useTheme()
  const { getCategoryLabel } = useCategoryLookup()

  // Função para mapear categoria da API para tradução usando o padrão unificado
  const mapCategoryToTranslation = (item: CategoryData): string => {
    const category = item.category

    if (!category) return ''

    if (item.categoryId != null) {
      return getCategoryLabel(type === 'expenses' ? 'EXPENSE' : 'REVENUE', item.categoryId, category)
    }

    if (type === 'expenses') {
      // Para despesas, usar as traduções padronizadas
      // Primeiro tentar como categoria
      const categoryKey = `expenses.categories.${category}`
      const categoryTranslation = t(categoryKey)

      if (categoryTranslation &&
          categoryTranslation !== categoryKey &&
          categoryTranslation !== category &&
          categoryTranslation.trim() !== '') {
        return categoryTranslation
      }

      // Depois tentar como subcategoria
      const subcategoryKey = `expenses.subcategories.${category}`
      const subcategoryTranslation = t(subcategoryKey)

      if (subcategoryTranslation &&
          subcategoryTranslation !== subcategoryKey &&
          subcategoryTranslation !== category &&
          subcategoryTranslation.trim() !== '') {
        return subcategoryTranslation
      }
    } else {
      // Para receitas, manter lógica existente
      const categoryType = 'revenues'
      const categoryKey = `categories.${categoryType}.category.${category}`
      const categoryTranslation = t(categoryKey)

      if (categoryTranslation && categoryTranslation !== categoryKey) {
        return categoryTranslation
      }

      const subcategoryKey = `categories.${categoryType}.subcategory.${category}`
      const subcategoryTranslation = t(subcategoryKey)

      if (subcategoryTranslation && subcategoryTranslation !== subcategoryKey) {
        return subcategoryTranslation
      }
    }

    // Fallback para categoria não mapeada - retorna o nome original
    return category
  }

  // Função para mapear categoria para cor baseada no índice
  const mapCategoryToColor = (category: string, index: number): string => {
    const colors = type === 'expenses' ? EXPENSE_COLORS : REVENUE_COLORS

    return colors[index % colors.length]
  }

  // Processar dados da API
  const processApiData = (apiData: CategoryData[]): CategoryData[] => {
    return apiData.map((item, index) => ({
      category: mapCategoryToTranslation(item),
      value: item.value,
      percentage: item.percentage,
      color: mapCategoryToColor(item.category, index)
    }))
  }

  // Dados padrão para fallback (somente quando não há dados da API)
  const getFallbackData = (): CategoryData[] => {
    if (type === 'expenses') {
      return []
    } else {
      return []
    }
  }

  const chartData = data && data.length > 0 ? processApiData(data) : getFallbackData()

  if (loading) {
    return (
      <Card sx={{ height: '100%', minHeight: 350 }}>
        <CardHeader
          title={<Skeleton variant="text" width={150} height={20} />}
        />
        <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Skeleton variant="circular" width={200} height={200} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, justifyContent: 'center' }}>
            {[...Array(5)].map((_, index) => (
              <Skeleton key={index} variant="text" width={60} height={12} />
            ))}
          </Box>
        </CardContent>
      </Card>
    )
  }

  // Se não há dados, exibir mensagem
  if (!chartData || chartData.length === 0) {
    return (
      <Card sx={{ height: '100%', minHeight: 350 }}>
        <CardHeader
          title={
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.95rem', textAlign: 'center' }}>
              {title}
            </Typography>
          }
          sx={{ pb: 1 }}
        />
        <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            Dados não disponíveis
          </Typography>
        </CardContent>
      </Card>
    )
  }

  // Calcular o valor total para o centro do donut
  const totalValue = chartData.reduce((sum, item) => sum + item.value, 0)

  // Preparar dados para ApexCharts
  const series = chartData.map(item => item.value)
  const labels = chartData.map(item => item.category)
  const colors = chartData.map(item => item.color || '#666')

  // Configuração do gráfico ApexCharts
  const options: ApexOptions = {
    chart: {
      type: 'donut',
      height: 280,
      fontFamily: 'Inter, sans-serif',
      foreColor: theme.palette.text.primary
    },
    labels: labels,
    colors: colors,
    legend: {
      show: true,
      position: 'bottom',
      fontSize: '12px',
      fontFamily: 'Inter, sans-serif',
      labels: {
        useSeriesColors: true,
        colors: Array(chartData.length).fill(theme.palette.text.primary)
      },
      markers: {
      },
      itemMargin: {
        horizontal: 6,
        vertical: 4
      },
      formatter: function (seriesName, opts) {
        const percentage = chartData[opts.seriesIndex]?.percentage || 0

        return `${seriesName}: ${percentage.toFixed(1)}%`
      }
    },
    dataLabels: {
      enabled: false
    },
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: false
            },
            value: {
              show: false
            },
            total: {
              show: true,
              fontSize: '14px',
              fontFamily: 'Inter, sans-serif',
              color: theme.palette.text.secondary,
              label: t('categoryChart.total'),
              formatter: function () {
                return new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                  notation: 'compact'
                }).format(totalValue)
              }
            }
          }
        }
      }
    },
    tooltip: {
      theme: theme.palette.mode,
      style: {
        fontSize: '12px',
        fontFamily: 'Inter, sans-serif'
      },
      y: {
        formatter: function (val, opts) {
          const percentage = chartData[opts.seriesIndex]?.percentage || 0

          return `${new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(val)} (${percentage.toFixed(1)}%)`
        }
      }
    },
    states: {
      hover: {
        filter: {
          type: 'lighten',

        }
      },
      active: {
        filter: {
          type: 'none'
        }
      }
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 240
          },
          legend: {
            fontSize: '11px',
            itemMargin: {
              horizontal: 4,
              vertical: 2
            }
          }
        }
      }
    ]
  }

  return (
    <Card sx={{ height: '100%', minHeight: 350 }}>
      <CardHeader
        title={
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.95rem', textAlign: 'center' }}>
            {title}
          </Typography>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0, flex: 1 }}>
        <Box sx={{ width: '100%', height: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AppReactApexCharts
            type='donut'
            height={280}
            width='100%'
            options={options}
            series={series}
          />
        </Box>
      </CardContent>
    </Card>
  )
}

export default CategoryChart
